import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  bool, func, number, node, string
} from 'prop-types';
import classnames from 'classnames';

import { noop } from '../../utils/promo-utils';
import { PromoSidecarNav } from './subcomponents/promo-sidecar-nav/PromoSidecarNav';
import { PromoSidecarCta } from './subcomponents/promo-sidecar-cta/PromoSidecarCta';
import { usePromoPresentation } from '../../contexts/PromoPresentationProvider';
import './PromoSidecar.scss';

export const PromoSidecar = ({
  isSidecarOpen,
  setIsSidecarOpen,
  zIndex,
  useSidecarMask,
  useSidecarNav,
  useSidecarCta,
  backText,
  closeDrawer,
  itemId,
  children,
  selected,
  onSelect,
  setQuantityWarningText,
  showQuantityWarningState
}) => {
  const { isAppleMobileDevice } = usePromoPresentation();

  const closeSidecar = useCallback((event) => {
    if (event) event.preventDefault();
    setIsSidecarOpen(false);
  }, [setIsSidecarOpen]);

  const closeAll = useCallback((event) => {
    if (event) event.preventDefault();
    closeDrawer();
    setIsSidecarOpen(false);
  }, [setIsSidecarOpen, closeDrawer]);

  useEffect(() => {
    if (useSidecarMask) {
      if (isSidecarOpen) {
        // IE doesn't support adding/removing with multiple arguments.
        document.body.classList.add('promo-stop-body-scroll');
        if (isAppleMobileDevice) document.body.classList.add('promo-stop-body-scroll--ios');
      } else {
        document.body.classList.remove('promo-stop-body-scroll');
        if (isAppleMobileDevice) document.body.classList.remove('promo-stop-body-scroll--ios');
      }
    }
  }, [isSidecarOpen, useSidecarMask, isAppleMobileDevice]);

  const maskClasses = classnames('promo-sidecar-mask', {
    'promo-sidecar-mask--open': isSidecarOpen,
  });

  return (
    <>
      {typeof window !== 'undefined' && createPortal(
        <>
          {useSidecarMask && (
            <div
              className={maskClasses}
              data-testid={`promo-sidecar-mask${isSidecarOpen ? ' promo-sidecar-mask--open' : ''}`}
              role="button"
              aria-label="close the promotion drawer"
              tabIndex={0}
              onClick={closeSidecar}
            />
          )}
          <aside
            style={{ zIndex }}
            className={`promo-sidecar${isSidecarOpen ? ' promo-sidecar--open' : ''}`}
          >
            {useSidecarNav && (
              <PromoSidecarNav backText={backText} closeSidecar={closeSidecar} closeDrawer={closeAll} />
            )}

            <section className={`promo-sidecar-body
            ${useSidecarCta ? 'promo-sidecar-body--with-cta' : ''}
            ${useSidecarNav ? 'promo-sidecar-body--with-nav' : ''}`}
            >
              {children}
            </section>

            {useSidecarCta && itemId && (
              <PromoSidecarCta
                itemId={itemId}
                onSelect={onSelect}
                selected={selected}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
            )}
          </aside>
        </>, document.body
      )}
    </>
  );
};

PromoSidecar.displayName = 'PromoSidecar';

PromoSidecar.propTypes = {
  isSidecarOpen: bool.isRequired,
  setIsSidecarOpen: func.isRequired,
  children: node.isRequired,
  zIndex: number,
  useSidecarMask: bool,
  useSidecarCta: bool,
  useSidecarNav: bool,
  backText: string,
  closeDrawer: func,
  itemId: string,
  selected: bool,
  onSelect: func,
  setQuantityWarningText: func,
  showQuantityWarningState: bool
};

PromoSidecar.defaultProps = {
  zIndex: 1000,
  useSidecarMask: false,
  useSidecarCta: false,
  useSidecarNav: false,
  backText: 'Back',
  closeDrawer: noop,
  itemId: '',
  selected: false,
  onSelect: noop,
  setQuantityWarningText: noop,
  showQuantityWarningState: false
};
